import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ArrowLeft,
  Search,
  Building2,
  MapPin,
  PlugZap,
  Wifi,
  WifiOff,
  AlertTriangle,
  BatteryCharging,
  Network,
  Loader2,
  ServerCrash,
  RefreshCw,
  Pencil,
  X,
  Check,
  Trash2,
  ArrowRightLeft,
  Zap,
  ChevronRight,
  ChevronsUpDown,
} from "lucide-react";
import Api from "../../services/Api";
import { queryKeys } from "../../lib/query";
import { fetchHierarchyCached } from "../../lib/hooks/useHierarchy";
import { fetchFleetDataCached } from "../../lib/context/FleetDataProvider";
import "./ManageStation.scss";
import "../fleet_dashboard/FleetDashboard.scss";
import CollapsibleSection from "../fleet_dashboard/CollapsibleSection";
import GeoMap from "../fleet_dashboard/charts/GeoMap";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider";
import { resolveChargerStatus, TreeNode, TreeLeaf, KpiBadge } from "./StationSharedComponents";
import { getAccentColor } from "../../lib/utils/constants";

const STATION_DETAIL_URL = "/service/iqevse-csms/stations/v1";
const SYSTEM_INFO_URL = "/service/systems";

/* ══════════════════════════════════════════════════════════════
   ManageStation — View / Edit / Delete individual station
   Route: /stations/manage/:stationId → view detail
   ══════════════════════════════════════════════════════════════ */
export default function ManageStation() {
  const { stationId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /* ── Station list (for autocomplete search) ── */
  const [stations, setStations] = useState([]);

  /* ── Single station detail ── */
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  /* Fetch detail: prefer hierarchy API (has nested sites[].chargers[]),
     fall back to individual station detail endpoint */
  const fetchDetail = useCallback(
    async id => {
      setDetailLoading(true);
      setDetailError(null);
      try {
        const arr = await fetchHierarchyCached(queryClient);
        const found = arr.find(s => String(s.stationId || s.id) === String(id));
        if (found) {
          setDetail(found);
          const filtered = arr.filter(s => (s.stationName || s.name || "").toLowerCase() !== "unassigned");
          setStations(filtered);
        } else {
          const data = await Api.get(`${STATION_DETAIL_URL}/${id}`);
          setDetail(data?.data || data);
        }
      } catch (err) {
        setDetailError(err?.response?.data?.message || err.message || "Failed to load station details");
      } finally {
        setDetailLoading(false);
      }
    },
    [queryClient]
  );

  useEffect(() => {
    if (stationId) {
      fetchDetail(stationId);
    }
  }, [stationId, fetchDetail]);

  /* ── Navigation helpers ── */
  const goToList = useCallback(() => {
    navigate("/stations/manage");
  }, [navigate]);

  const goToStation = useCallback(
    id => {
      navigate(`/stations/manage/${id}`);
    },
    [navigate]
  );

  if (detailLoading) {
    return (
      <div className="manage-station__state">
        <motion.div
          className="manage-station__state-icon manage-station__state-icon--loading"
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
        >
          <Loader2 size={36} />
        </motion.div>
        <h3 className="manage-station__state-title">Loading Station Details...</h3>
      </div>
    );
  }

  if (detailError) {
    return (
      <div className="manage-station__state">
        <div className="manage-station__state-icon manage-station__state-icon--error">
          <ServerCrash size={36} />
        </div>
        <h3 className="manage-station__state-title">Failed to load station</h3>
        <p className="manage-station__state-desc">{detailError}</p>
        <button className="manage-station__state-btn" onClick={() => fetchDetail(stationId)}>
          <RefreshCw size={16} /> Retry
        </button>
      </div>
    );
  }

  if (!detail) return null;

  return (
    <StationDetailView
      station={detail}
      allStations={stations}
      onBack={goToList}
      onRefresh={() => fetchDetail(stationId)}
      onNavigate={goToStation}
    />
  );
}

/* ══════════════════════════════════════════════════════════════
   Station Detail View — inline View / Edit toggle,
                          KPIs, details, hierarchy, chargers,
                          Delete action
   ══════════════════════════════════════════════════════════════ */
function StationDetailView({ station, allStations, onBack, onRefresh, onNavigate }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setState: setGlobalFilterState } = useGlobalFilters();
  const name = station.stationName || station.name || "Unnamed";
  const id = station.stationId || station.id;
  const address = station.address || "";
  const siteIds = useMemo(() => station.siteIds || [], [station.siteIds]);
  const sitesData = useMemo(() => station.sites || [], [station.sites]);
  const latitude = station.latitude || station.lat;
  const longitude = station.longitude || station.lng;
  const hasStationMap = latitude != null && longitude != null;

  /* ── Inline edit state ── */
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(name);
  const [editMaxBreakerCurrent, setEditMaxBreakerCurrent] = useState(station.maxBreakerCurrent ?? "");
  const [editAddress, setEditAddress] = useState(address);
  const [editLat, setEditLat] = useState(latitude || 37.7749);
  const [editLng, setEditLng] = useState(longitude || -122.4194);
  const [editCity, setEditCity] = useState("");
  const [editState, setEditState] = useState("");
  const [editZip, setEditZip] = useState("");
  const [editSiteIds, setEditSiteIds] = useState(siteIds.length > 0 ? siteIds : sitesData.map(s => s.siteId || s.id));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  /* ──/* Fetch available sites for add-site in edit mode (unassigned only) with ev_charger API fallback */
  const [availableSites, setAvailableSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [siteSearch, setSiteSearch] = useState("");

  useEffect(() => {
    if (!isEditing) return;
    let cancelled = false;
    setSitesLoading(true);

    async function fetchAvailableSites() {
      try {
        // Use cached hierarchy data (shared via React Query)
        const stationsArr = await fetchHierarchyCached(queryClient);
        if (cancelled) return;

        // Find the "unassigned" station and extract its sites
        const unassignedStation = stationsArr.find(
          station => (station.stationName || station.name || "").toLowerCase() === "unassigned"
        );

        if (unassignedStation && unassignedStation.sites && unassignedStation.sites.length > 0) {
          // Use only unassigned sites from hierarchy API
          const siteMap = new Map();
          unassignedStation.sites.forEach(site => {
            const sid = site.siteId || site.id;
            if (!sid || siteMap.has(sid)) return;
            const chargers = site.chargers || [];
            siteMap.set(sid, {
              siteId: sid,
              displayName: site.siteName || site.displayName || site.name || `Site ${sid}`,
              country: site.country || "",
              chargerCount: chargers.length,
            });
          });
          setAvailableSites(Array.from(siteMap.values()));
        } else {
          // Fallback to ev_charger cached data (allow all sites)
          const chargers = await fetchFleetDataCached(queryClient).catch(() => []);
          if (cancelled) return;

          const siteMap = new Map();
          chargers.forEach(c => {
            const sid = c.siteId;
            if (!sid) return;
            if (!siteMap.has(sid)) {
              siteMap.set(sid, {
                siteId: sid,
                displayName: c.siteName || `Site ${sid}`,
                country: c.country || "",
                chargerCount: 0,
              });
            }
            siteMap.get(sid).chargerCount += 1;
          });
          setAvailableSites(Array.from(siteMap.values()));
        }
      } catch {
        // Hierarchy failed — fallback to ev_charger cached data
        try {
          const chargers = await fetchFleetDataCached(queryClient);
          if (cancelled) return;

          const siteMap = new Map();
          chargers.forEach(c => {
            const sid = c.siteId;
            if (!sid) return;
            if (!siteMap.has(sid)) {
              siteMap.set(sid, {
                siteId: sid,
                displayName: c.siteName || `Site ${sid}`,
                country: c.country || "",
                chargerCount: 0,
              });
            }
            siteMap.get(sid).chargerCount += 1;
          });
          setAvailableSites(Array.from(siteMap.values()));
        } catch {
          // Both APIs failed — availableSites remain empty
        }
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    }

    fetchAvailableSites();
    return () => {
      cancelled = true;
    };
  }, [isEditing, queryClient]);

  /* Reset edit state when toggling to edit mode */
  const enterEditMode = useCallback(() => {
    setEditName(name);
    setEditMaxBreakerCurrent(station.maxBreakerCurrent ?? "");
    setEditAddress(address);
    setEditLat(latitude || 37.7749);
    setEditLng(longitude || -122.4194);
    setEditCity("");
    setEditState("");
    setEditZip("");
    setEditSiteIds(siteIds.length > 0 ? siteIds : sitesData.map(s => s.siteId || s.id));
    setSaveError(null);
    setSiteSearch("");
    setIsEditing(true);
  }, [name, station, address, latitude, longitude, siteIds, sitesData]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setSaveError(null);
  }, []);

  const editLocationUpdate = useCallback((key, value) => {
    if (key === "address") setEditAddress(value);
    else if (key === "lat") setEditLat(value);
    else if (key === "lng") setEditLng(value);
    else if (key === "city") setEditCity(value);
    else if (key === "state") setEditState(value);
    else if (key === "zip") setEditZip(value);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const fullAddress = [editAddress, editCity, editState, editZip].filter(Boolean).join(", ").trim();
      const payload = {
        ...station,
        stationName: editName.trim(),
        siteIds: editSiteIds.map(sid => (typeof sid === "string" ? parseInt(sid, 10) : sid)),
        address: fullAddress || station.address || "",
        latitude: editLat,
        longitude: editLng,
      };
      const parsedBreaker = Number(editMaxBreakerCurrent);
      if (
        editMaxBreakerCurrent !== "" &&
        editMaxBreakerCurrent != null &&
        !isNaN(parsedBreaker) &&
        parsedBreaker >= 0
      ) {
        payload.maxBreakerCurrent = parsedBreaker;
      } else {
        delete payload.maxBreakerCurrent;
      }
      await Api.put(`${STATION_DETAIL_URL}/${id}`, payload);
      setIsEditing(false);
      onRefresh();
    } catch (err) {
      setSaveError(err?.response?.data?.message || err.message || "Failed to save station");
    } finally {
      setSaving(false);
    }
  }, [
    station,
    editName,
    editMaxBreakerCurrent,
    editAddress,
    editCity,
    editState,
    editZip,
    editLat,
    editLng,
    editSiteIds,
    id,
    onRefresh,
  ]);

  const toggleEditSite = useCallback(siteId => {
    setEditSiteIds(prev => (prev.includes(siteId) ? prev.filter(s => s !== siteId) : [...prev, siteId]));
  }, []);

  const filteredAvailSites = useMemo(() => {
    if (!siteSearch.trim()) return availableSites;
    const q = siteSearch.toLowerCase();
    return availableSites.filter(
      s =>
        String(s.siteId).toLowerCase().includes(q) ||
        s.displayName.toLowerCase().includes(q) ||
        s.country.toLowerCase().includes(q)
    );
  }, [siteSearch, availableSites]);

  /* Collect all chargers from sites */
  const allChargers = useMemo(() => {
    const chargers = [];
    sitesData.forEach(site => {
      (site.chargers || []).forEach(chg => {
        chargers.push({
          ...chg,
          siteId: site.siteId || site.id,
          siteName: site.displayName || site.name || `Site ${site.siteId || site.id}`,
        });
      });
    });
    return chargers;
  }, [sitesData]);

  /* Charger stats — computed entirely from hierarchy API data */
  const chargerStats = useMemo(() => {
    const total = allChargers.length;
    let online = 0,
      charging = 0,
      faulted = 0,
      offline = 0,
      totalRatedCurrent = 0;
    allChargers.forEach(c => {
      if (c.isConnected) online++;
      else offline++;
      totalRatedCurrent += c.ratedCurrent || c.maxCurrent || 0;
    });
    return { total, online, charging, faulted, offline, totalRatedCurrent };
  }, [allChargers]);

  const maxBreakerCurrent = station.maxBreakerCurrent;
  const remainingCapacity = maxBreakerCurrent != null ? maxBreakerCurrent - chargerStats.totalRatedCurrent : null;

  /* ── Delete modal ── */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await Api.delete(`${STATION_DETAIL_URL}/${id}`);
      navigate("/stations/manage");
    } catch (err) {
      setDeleteError(err?.response?.data?.message || err.message || "Failed to delete station");
    } finally {
      setDeleting(false);
    }
  };

  /* ── Move Sites modal ── */
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveSiteId, setMoveSiteId] = useState(null);
  const [moveTargetStation, setMoveTargetStation] = useState("");
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState(null);

  const otherStations = useMemo(() => {
    return (allStations || []).filter(s => String(s.stationId || s.id) !== String(id));
  }, [allStations, id]);

  const handleMoveSite = async () => {
    if (!moveSiteId || !moveTargetStation) return;
    setMoving(true);
    setMoveError(null);
    try {
      await Api.put(`${STATION_DETAIL_URL}/${moveTargetStation}/sites`, {
        siteIds: [moveSiteId],
        sourceStationId: id,
      });
      setShowMoveModal(false);
      setMoveSiteId(null);
      setMoveTargetStation("");
      onRefresh();
    } catch (err) {
      setMoveError(err?.response?.data?.message || err.message || "Failed to move site");
    } finally {
      setMoving(false);
    }
  };

  /* ── Autocomplete search ── */
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return (allStations || [])
      .filter(
        s => (s.stationName || s.name || "").toLowerCase().includes(q) || (s.address || "").toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [searchQuery, allStations]);

  useEffect(() => {
    const handleClick = e => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const canSave = editName.trim().length > 0 && editSiteIds.length > 0;

  /* ── Collapsible section state (all collapsed by default) ── */
  const DETAIL_SECTION_KEYS = ["stationHierarchy", "siteLocations", "chargersOverview"];
  const [sectionOpen, setSectionOpen] = useState(() =>
    DETAIL_SECTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: false }), {})
  );
  const toggleSection = useCallback(key => {
    setSectionOpen(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const allSectionsExpanded = DETAIL_SECTION_KEYS.every(k => sectionOpen[k]);
  const handleToggleAllSections = useCallback(() => {
    const next = !allSectionsExpanded;
    setSectionOpen(DETAIL_SECTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: next }), {}));
  }, [allSectionsExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Fetch systemInfo per site (triggered when Sites Overview is expanded) ── */
  const [siteInfoMap, setSiteInfoMap] = useState({});
  const [siteInfoLoading, setSiteInfoLoading] = useState(false);
  const siteInfoFetchedRef = useRef(false);

  useEffect(() => {
    if (!sectionOpen.siteLocations) return;
    if (siteInfoFetchedRef.current) return;
    const allSiteIds = sitesData.length > 0 ? sitesData.map(s => s.siteId || s.id) : siteIds;
    if (allSiteIds.length === 0) return;

    let cancelled = false;
    siteInfoFetchedRef.current = true;
    setSiteInfoLoading(true);

    const fetchSiteInfo = async sid => {
      try {
        const info = await queryClient.fetchQuery({
          queryKey: queryKeys.sites.systemInfo(sid),
          queryFn: () => Api.get(`${SYSTEM_INFO_URL}/${sid}/systeminfo`),
          staleTime: 10 * 60 * 1000,
        });
        return { siteId: sid, info };
      } catch {
        return { siteId: sid, info: null };
      }
    };

    Promise.all(allSiteIds.map(fetchSiteInfo))
      .then(results => {
        if (cancelled) return;
        const map = {};
        results.forEach(({ siteId: sid, info }) => {
          if (!info) return;
          const addr = info.address || {};
          const parts = [
            addr.address1 || addr.street,
            addr.city,
            addr.state || addr.province,
            addr.country,
            addr.zip || addr.postalCode,
          ].filter(Boolean);

          // Extract coordinates — API may return:
          //   address.location: [lng, lat] array OR { latitude, longitude } object
          //   address.geolocation: { coordinates: [lng, lat] } OR { latitude, longitude }
          //   top-level: info.latitude / info.longitude
          let lat = null;
          let lng = null;
          const loc = addr.location;
          if (Array.isArray(loc) && loc.length >= 2) {
            // GeoJSON convention: [longitude, latitude]
            lng = parseFloat(loc[0]);
            lat = parseFloat(loc[1]);
          } else if (loc && typeof loc === "object") {
            lat = parseFloat(loc.latitude ?? loc.lat ?? null);
            lng = parseFloat(loc.longitude ?? loc.lng ?? loc.lon ?? null);
          }
          const geo = addr.geolocation;
          if ((lat == null || isNaN(lat)) && geo && typeof geo === "object") {
            const coords = geo.coordinates;
            if (Array.isArray(coords) && coords.length >= 2) {
              lng = parseFloat(coords[0]);
              lat = parseFloat(coords[1]);
            } else {
              lat = parseFloat(geo.latitude ?? geo.lat ?? null);
              lng = parseFloat(geo.longitude ?? geo.lng ?? geo.lon ?? null);
            }
          }
          if ((lat == null || isNaN(lat)) && info.latitude != null) {
            lat = parseFloat(info.latitude);
            lng = parseFloat(info.longitude);
          }

          map[sid] = {
            address: parts.join(", "),
            city: addr.city || "",
            country: addr.country || "",
            timezone: addr.timezone || "",
            createdAt: info.createdAt || info.created_at || info.createdDate || "",
            lat: isNaN(lat) ? null : lat,
            lng: isNaN(lng) ? null : lng,
          };
        });
        setSiteInfoMap(map);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSiteInfoLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sectionOpen.siteLocations, sitesData, siteIds, queryClient]);

  /* ── Build siteLocations for GeoMap (same shape as useChargerLocations) ── */
  const stationSiteLocations = useMemo(() => {
    const sites = sitesData.length > 0 ? sitesData : siteIds.map(sid => ({ siteId: sid }));
    return sites.map(site => {
      const sid = site.siteId || site.id;
      const info = siteInfoMap[sid] || {};
      const sChargers = site.chargers || [];
      return {
        siteId: sid,
        lat: info.lat ?? null,
        lng: info.lng ?? null,
        isApproximate: false,
        chargerCount: sChargers.length,
        onlineCount: sChargers.filter(c => c.isConnected).length,
        chargers: sChargers,
        siteName: site.siteName || site.displayName || site.name || info.city || `Site ${sid}`,
        address: info.address || site.address || "",
        country: info.country || site.country || "",
        state: info.state || "",
        city: info.city || "",
        timezone: info.timezone || "",
      };
    });
  }, [sitesData, siteIds, siteInfoMap]);

  return (
    <div className="manage-station__detail">
      {/* Top bar: back + search */}
      <div className="manage-station__detail-top-bar">
        <button className="manage-station__back-btn" onClick={onBack}>
          <ArrowLeft size={16} /> All Stations
        </button>
        <div className="manage-station__autocomplete" ref={searchRef}>
          <div className="manage-station__autocomplete-input">
            <Search size={15} />
            <input
              type="text"
              placeholder="Search station by name or address..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => searchQuery.trim() && setShowDropdown(true)}
            />
            {searchQuery && (
              <button
                className="manage-station__autocomplete-clear"
                onClick={() => {
                  setSearchQuery("");
                  setShowDropdown(false);
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>
          {showDropdown && searchResults.length > 0 && (
            <div className="manage-station__autocomplete-dropdown">
              {searchResults.map(s => {
                const sName = s.stationName || s.name || "Unnamed";
                const sId = s.stationId || s.id;
                const sAddr = s.address || "";
                return (
                  <div
                    key={sId}
                    className="manage-station__autocomplete-item"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setShowDropdown(false);
                      setSearchQuery("");
                      onNavigate(sId);
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowDropdown(false);
                        setSearchQuery("");
                        onNavigate(sId);
                      }
                    }}
                  >
                    <Network size={14} className="manage-station__autocomplete-item-icon" />
                    <div>
                      <div className="manage-station__autocomplete-item-name">{sName}</div>
                      {sAddr && <div className="manage-station__autocomplete-item-addr">{sAddr}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Station Info Card ── */}
      <div className="manage-station__info-card">
        <div className="manage-station__detail-header">
          <div className="manage-station__detail-title-row">
            <Network size={24} className="manage-station__detail-icon" />
            <div>
              {isEditing ? (
                <input
                  className="manage-station__inline-input manage-station__inline-input--name"
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  placeholder="Station name"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
              ) : (
                <h2 className="manage-station__detail-name">{name}</h2>
              )}
              <p className="manage-station__detail-meta">
                {station.createdAt ? <>Created {station.createdAt}</> : "Station"}
              </p>
            </div>
          </div>
          <div className="manage-station__detail-actions">
            {/* Segmented View / Edit toggle */}
            <div className="manage-station__mode-toggle">
              <button
                className={`manage-station__mode-btn ${!isEditing ? "manage-station__mode-btn--active" : ""}`}
                onClick={cancelEdit}
              >
                <Search size={13} /> View
              </button>
              <button
                className={`manage-station__mode-btn ${isEditing ? "manage-station__mode-btn--active" : ""}`}
                onClick={enterEditMode}
              >
                <Pencil size={13} /> Edit
              </button>
            </div>
            <button
              className="manage-station__action-btn manage-station__action-btn--danger"
              onClick={() => setShowDeleteModal(true)}
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </div>

        {isEditing ? (
          <>
            <div className="manage-station__kpis">
              <KpiBadge icon={Building2} value={siteIds.length || sitesData.length} label="Sites" color="teal" />
              <KpiBadge icon={PlugZap} value={chargerStats.total} label="Chargers" color="orange" />
              <KpiBadge icon={Wifi} value={chargerStats.online} label="Online" color="green" />
              <KpiBadge icon={BatteryCharging} value={chargerStats.charging} label="Charging" color="blue" />
              <KpiBadge icon={AlertTriangle} value={chargerStats.faulted} label="Faulted" color="red" />
            </div>

            <div className="manage-station__breaker-edit">
              <label htmlFor="edit-max-breaker" className="manage-station__breaker-edit-label">
                Max Breaker Current (A)
              </label>
              <input
                id="edit-max-breaker"
                className="manage-station__inline-input manage-station__breaker-edit-input"
                type="number"
                min="0"
                step="1"
                placeholder="e.g. 120"
                value={editMaxBreakerCurrent}
                onChange={e => setEditMaxBreakerCurrent(e.target.value)}
              />
            </div>

            <div className="manage-station__detail-rows">
              <EditLocationMap
                lat={editLat}
                lng={editLng}
                address={editAddress}
                city={editCity}
                state={editState}
                zip={editZip}
                update={editLocationUpdate}
              />
            </div>
          </>
        ) : (
          <div
            className={`manage-station__overview-layout ${hasStationMap ? "" : "manage-station__overview-layout--single"}`}
          >
            <div className="manage-station__overview-main">
              <div className="manage-station__kpis manage-station__kpis--overview">
                <KpiBadge icon={Building2} value={siteIds.length || sitesData.length} label="Sites" color="teal" />
                <KpiBadge icon={PlugZap} value={chargerStats.total} label="Chargers" color="orange" />
                <KpiBadge icon={Wifi} value={chargerStats.online} label="Online" color="green" />
                <KpiBadge icon={BatteryCharging} value={chargerStats.charging} label="Charging" color="blue" />
                <KpiBadge icon={AlertTriangle} value={chargerStats.faulted} label="Faulted" color="red" />
                {maxBreakerCurrent != null && (
                  <KpiBadge icon={Zap} value={`${maxBreakerCurrent}A`} label="Max Breaker" color="orange" />
                )}
                {remainingCapacity != null && (
                  <KpiBadge
                    icon={Zap}
                    value={`${remainingCapacity}A`}
                    label="Remaining"
                    color={remainingCapacity >= 0 ? "green" : "red"}
                  />
                )}
              </div>

              {!hasStationMap && address && (
                <div className="manage-station__detail-rows manage-station__detail-rows--overview">
                  <div className="manage-station__detail-row">
                    <MapPin size={15} /> <span>{address}</span>
                  </div>
                </div>
              )}
            </div>

            {hasStationMap && (
              <div className="manage-station__overview-map-panel">
                {address && (
                  <div className="manage-station__detail-row manage-station__detail-row--map-panel">
                    <MapPin size={15} /> <span>{address}</span>
                  </div>
                )}
                <StaticLocationMap lat={latitude} lng={longitude} label={name} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Inline Edit: Site selection ── */}
      {isEditing && (
        <motion.div
          className="manage-station__edit-sites-section"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="manage-station__section-title">
            <PlugZap size={18} /> <span>Edit Sites ({editSiteIds.length} selected)</span>
          </div>

          {/* Selected site chips */}
          {editSiteIds.length > 0 && (
            <div className="manage-station__edit-chips">
              {editSiteIds.map(sid => {
                const found =
                  availableSites.find(s => s.siteId === sid) || sitesData.find(s => (s.siteId || s.id) === sid);
                const label = found?.displayName || found?.name || `Site ${sid}`;
                return (
                  <span key={sid} className="manage-station__edit-chip">
                    <PlugZap size={11} /> {label}
                    <button onClick={() => toggleEditSite(sid)}>
                      <X size={11} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Site search */}
          <div className="manage-station__edit-site-search">
            <Search size={15} />
            <input
              type="text"
              placeholder="Search sites by ID, name, or country..."
              value={siteSearch}
              onChange={e => setSiteSearch(e.target.value)}
            />
          </div>

          {/* Site list */}
          <div className="manage-station__edit-site-list">
            {sitesLoading ? (
              <div className="manage-station__edit-site-loading">
                <Loader2 size={18} className="manage-station__spinner" /> Loading sites...
              </div>
            ) : (
              <>
                {filteredAvailSites.map(site => {
                  const selected = editSiteIds.includes(site.siteId);
                  return (
                    <div
                      key={site.siteId}
                      className={`manage-station__edit-site-item ${selected ? "manage-station__edit-site-item--selected" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleEditSite(site.siteId)}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleEditSite(site.siteId);
                        }
                      }}
                    >
                      <div className="manage-station__edit-site-check">{selected && <Check size={13} />}</div>
                      <div className="manage-station__edit-site-info">
                        <span className="manage-station__edit-site-name">{site.displayName}</span>
                        <span className="manage-station__edit-site-meta">
                          Site ID: {site.siteId} &middot; {site.country || "N/A"} &middot; {site.chargerCount} chargers
                        </span>
                      </div>
                    </div>
                  );
                })}
                {filteredAvailSites.length === 0 && (
                  <div className="manage-station__edit-site-empty">No sites match your search.</div>
                )}
              </>
            )}
          </div>

          {/* Save / Cancel bar */}
          {saveError && (
            <div className="manage-station__modal-error" style={{ marginBottom: 12 }}>
              <AlertTriangle size={14} /> {saveError}
            </div>
          )}
          <div className="manage-station__edit-actions">
            <button className="manage-station__action-btn" onClick={cancelEdit}>
              <X size={14} /> Cancel
            </button>
            <button
              className="manage-station__action-btn manage-station__action-btn--save"
              disabled={!canSave || saving}
              onClick={handleSave}
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="manage-station__spinner" /> Saving...
                </>
              ) : (
                <>
                  <Check size={14} /> Save Changes
                </>
              )}
            </button>
          </div>
        </motion.div>
      )}

      {/* ── Expand All / Collapse All ── */}
      {!isEditing && (
        <div className="manage-station__section-controls">
          <button type="button" className="manage-station__section-controls-btn" onClick={handleToggleAllSections}>
            <ChevronsUpDown size={14} />
            {allSectionsExpanded ? "Collapse All" : "Expand All"}
          </button>
        </div>
      )}

      {/* ── Station Overview ── */}
      {!isEditing && (
        <CollapsibleSection
          title="Station Overview"
          icon={Network}
          isOpen={sectionOpen.stationHierarchy}
          onToggle={() => toggleSection("stationHierarchy")}
        >
          <div className="hierarchy-tree">
            <TreeNode icon={<Network size={16} />} label={name} tag="Station" tagColor="orange" defaultOpen>
              {sitesData.length > 0
                ? sitesData.map(site => (
                    <TreeNode
                      key={site.siteId || site.id}
                      icon={<Building2 size={14} />}
                      label={site.siteName || site.displayName || site.name || `Site ${site.siteId || site.id}`}
                      tag="Site"
                      tagColor="teal"
                      meta={(site.chargers || []).length + " chargers" + (site.country ? " · " + site.country : "")}
                    >
                      {(site.chargers || []).map(chg => (
                        <TreeLeaf key={chg.id || chg.serialNumber} charger={chg} />
                      ))}
                    </TreeNode>
                  ))
                : siteIds.map(sid => (
                    <TreeNode
                      key={sid}
                      icon={<Building2 size={14} />}
                      label={`Site ${sid}`}
                      tag="Site"
                      tagColor="teal"
                      meta={`Site ID: ${sid}`}
                    />
                  ))}
            </TreeNode>
          </div>
        </CollapsibleSection>
      )}

      {/* ── Sites Overview ── */}
      {!isEditing && (
        <CollapsibleSection
          title={`Sites Overview (${sitesData.length || siteIds.length})`}
          icon={MapPin}
          isOpen={sectionOpen.siteLocations}
          onToggle={() => toggleSection("siteLocations")}
        >
          <GeoMap
            siteLocations={stationSiteLocations}
            locationsLoading={siteInfoLoading}
            onMoveSite={sId => {
              setMoveSiteId(sId);
              setMoveTargetStation("");
              setMoveError(null);
              setShowMoveModal(true);
            }}
          />
        </CollapsibleSection>
      )}

      {/* ── Chargers Overview ── */}
      {!isEditing && allChargers.length > 0 && (
        <CollapsibleSection
          title={`Chargers Overview (${allChargers.length})`}
          icon={Zap}
          isOpen={sectionOpen.chargersOverview}
          onToggle={() => toggleSection("chargersOverview")}
        >
          <div className="manage-station__charger-summary">
            <div className="manage-station__charger-stat manage-station__charger-stat--online">
              <Wifi size={16} />
              <span className="manage-station__charger-stat-value">{chargerStats.online}</span>
              <span className="manage-station__charger-stat-label">Online</span>
            </div>
            <div className="manage-station__charger-stat manage-station__charger-stat--charging">
              <BatteryCharging size={16} />
              <span className="manage-station__charger-stat-value">{chargerStats.charging}</span>
              <span className="manage-station__charger-stat-label">Charging</span>
            </div>
            <div className="manage-station__charger-stat manage-station__charger-stat--faulted">
              <AlertTriangle size={16} />
              <span className="manage-station__charger-stat-value">{chargerStats.faulted}</span>
              <span className="manage-station__charger-stat-label">Faulted</span>
            </div>
            <div className="manage-station__charger-stat manage-station__charger-stat--offline">
              <WifiOff size={16} />
              <span className="manage-station__charger-stat-value">{chargerStats.offline}</span>
              <span className="manage-station__charger-stat-label">Offline</span>
            </div>
          </div>

          <div className="manage-station__charger-grid">
            {allChargers.map((chg, i) => {
              const serial = chg.serialNumber || chg.serial || chg.id;
              const chgSiteId = chg.siteId || "";
              const model = chg.modelName || chg.model || chg.sku || "";
              const status = resolveChargerStatus(chg);
              const power = chg.power || chg.maxPower || "";

              const handleChargerNav = () => {
                setGlobalFilterState({ serialNumber: serial, siteId: chgSiteId, isSerialNumberConfirmed: true });
                navigate(`/chargers/${serial}`);
              };

              return (
                <motion.div
                  key={serial || i}
                  className={`manage-station__charger-card manage-station__charger-card--${status}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  onClick={handleChargerNav}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === "Enter") handleChargerNav();
                  }}
                >
                  <div className="manage-station__charger-card-top">
                    <PlugZap size={16} className="manage-station__charger-card-icon" />
                    <span
                      className={`manage-station__charger-card-status manage-station__charger-card-status--${status}`}
                    >
                      {status}
                    </span>
                  </div>
                  <div className="manage-station__charger-card-serial">{serial}</div>
                  {model && <div className="manage-station__charger-card-model">{model}</div>}
                  <div className="manage-station__charger-card-footer">
                    {chg.ratedCurrent || chg.maxCurrent ? (
                      <span className="manage-station__charger-card-power">
                        <Zap size={11} /> {chg.ratedCurrent || chg.maxCurrent}A
                      </span>
                    ) : power ? (
                      <span className="manage-station__charger-card-power">{power} kW</span>
                    ) : null}
                    <span className="manage-station__charger-card-site">{chg.siteName}</span>
                  </div>
                  <div className="manage-station__charger-card-link">
                    View Summary <ChevronRight size={12} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </CollapsibleSection>
      )}

      {/* ── Move Site Modal ── */}
      <AnimatePresence>
        {showMoveModal && (
          <motion.div
            className="manage-station__modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowMoveModal(false)}
          >
            <motion.div
              className="manage-station__modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="manage-station__modal-header">
                <h3>
                  <ArrowRightLeft size={18} /> Move Site
                </h3>
                <button onClick={() => setShowMoveModal(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className="manage-station__modal-body">
                <p className="manage-station__modal-desc">
                  Move <strong>Site {moveSiteId}</strong> from <strong>{name}</strong> to another station:
                </p>
                <select
                  className="manage-station__modal-select"
                  value={moveTargetStation}
                  onChange={e => setMoveTargetStation(e.target.value)}
                >
                  <option value="">Select target station...</option>
                  {otherStations.map(s => (
                    <option key={s.stationId || s.id} value={s.stationId || s.id}>
                      {s.stationName || s.name || `Station ${s.stationId || s.id}`}
                    </option>
                  ))}
                </select>
                {moveError && (
                  <div className="manage-station__modal-error">
                    <AlertTriangle size={14} /> {moveError}
                  </div>
                )}
              </div>
              <div className="manage-station__modal-footer">
                <button
                  className="manage-station__modal-btn manage-station__modal-btn--cancel"
                  onClick={() => setShowMoveModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="manage-station__modal-btn manage-station__modal-btn--confirm"
                  disabled={!moveTargetStation || moving}
                  onClick={handleMoveSite}
                >
                  {moving ? (
                    <>
                      <Loader2 size={14} className="manage-station__spinner" /> Moving...
                    </>
                  ) : (
                    <>
                      <Check size={14} /> Move Site
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete Confirmation Modal ── */}
      <DeleteConfirmModal
        show={showDeleteModal}
        stationName={name}
        deleting={deleting}
        error={deleteError}
        onConfirm={handleDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteError(null);
        }}
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Delete Confirmation Modal
   ══════════════════════════════════════════════════════════════ */
function DeleteConfirmModal({ show, stationName, deleting, error, onConfirm, onCancel }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="manage-station__modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onCancel}
        >
          <motion.div
            className="manage-station__modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="manage-station__modal-header manage-station__modal-header--danger">
              <h3>
                <Trash2 size={18} /> Delete Station
              </h3>
              <button onClick={onCancel}>
                <X size={18} />
              </button>
            </div>
            <div className="manage-station__modal-body">
              <p className="manage-station__modal-desc">
                Are you sure you want to delete <strong>{stationName}</strong>? This action cannot be undone. All site
                associations will be removed.
              </p>
              {error && (
                <div className="manage-station__modal-error">
                  <AlertTriangle size={14} /> {error}
                </div>
              )}
            </div>
            <div className="manage-station__modal-footer">
              <button className="manage-station__modal-btn manage-station__modal-btn--cancel" onClick={onCancel}>
                Cancel
              </button>
              <button
                className="manage-station__modal-btn manage-station__modal-btn--danger"
                disabled={deleting}
                onClick={onConfirm}
              >
                {deleting ? (
                  <>
                    <Loader2 size={14} className="manage-station__spinner" /> Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} /> Delete Station
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ══════════════════════════════════════════════════════════════
   Static Location Map — read-only mini map for station overview
   ══════════════════════════════════════════════════════════════ */
function StaticLocationMap({ lat, lng, label }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Defer map init until the container is actually visible (non-zero size)
    const initMap = () => {
      if (mapRef.current) return;
      const el = containerRef.current;
      if (!el || el.offsetHeight === 0 || el.offsetWidth === 0) return;

      const map = L.map(el, {
        center: [lat, lng],
        zoom: 13,
        scrollWheelZoom: true,
        dragging: true,
        doubleClickZoom: true,
        zoomControl: true,
        attributionControl: false,
      });

      L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
        subdomains: "abcd",
        maxZoom: 19,
      }).addTo(map);

      L.circleMarker([lat, lng], {
        radius: 8,
        fillColor: getAccentColor(),
        fillOpacity: 0.85,
        color: "#fff",
        weight: 2,
      })
        .addTo(map)
        .bindTooltip(label || "Station", { permanent: false, direction: "top", offset: [0, -10] });

      mapRef.current = map;

      // After init, also schedule an invalidateSize for good measure
      setTimeout(() => map.invalidateSize(), 200);
    };

    // Try immediately
    initMap();

    // If container was zero-size (collapsed section), use ResizeObserver to detect when it becomes visible
    if (!mapRef.current) {
      observerRef.current = new ResizeObserver(() => {
        initMap();
        if (mapRef.current && observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
      });
      observerRef.current.observe(containerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Also invalidate map size whenever the container resizes (e.g. section expand)
  useEffect(() => {
    if (!mapRef.current) return;
    const timer = setTimeout(() => mapRef.current?.invalidateSize(), 400);
    return () => clearTimeout(timer);
  });

  return <div ref={containerRef} className="manage-station__static-map" />;
}

StaticLocationMap.propTypes = {
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  label: PropTypes.string,
};

/* ══════════════════════════════════════════════════════════════
   Edit Location Map — inline geomap for edit station mode
   ══════════════════════════════════════════════════════════════ */
function EditLocationMap({ lat, lng, address, city, state, zip, update }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const geocodeTimerRef = useRef(null);

  const reverseGeocode = useCallback(
    async (rlat, rlng) => {
      clearTimeout(geocodeTimerRef.current);
      geocodeTimerRef.current = setTimeout(async () => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${rlat}&lon=${rlng}&format=json&addressdetails=1`,
            { headers: { "Accept-Language": "en" } }
          );
          const data = await res.json();
          if (data?.address) {
            const a = data.address;
            const road = [a.house_number, a.road].filter(Boolean).join(" ");
            update("address", road || a.neighbourhood || a.suburb || "");
            update("city", a.city || a.town || a.village || a.county || "");
            update("state", a.state || a.region || "");
            update("zip", a.postcode || "");
          }
        } catch {
          // Reverse geocode failed — silently ignore
        }
      }, 400);
    },
    [update]
  );

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [lat, lng],
      zoom: 12,
      scrollWheelZoom: true,
      attributionControl: false,
    });

    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      subdomains: "abcd",
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker([lat, lng], {
      draggable: true,
      icon: L.divIcon({
        className: "create-hub__map-pin",
        html: `<div class="create-hub__pin-inner"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${getAccentColor()}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 24],
      }),
    }).addTo(map);

    marker.on("dragend", () => {
      const pos = marker.getLatLng();
      update("lat", pos.lat);
      update("lng", pos.lng);
      reverseGeocode(pos.lat, pos.lng);
    });

    map.on("click", e => {
      marker.setLatLng(e.latlng);
      update("lat", e.latlng.lat);
      update("lng", e.latlng.lng);
      reverseGeocode(e.latlng.lat, e.latlng.lng);
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      clearTimeout(geocodeTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="manage-station__edit-location">
      <div className="create-hub__map-wrapper">
        <div ref={mapContainerRef} className="create-hub__map" />
        <div className="create-hub__coords">
          <span>Lat: {lat.toFixed(4)}</span>
          <span>Lng: {lng.toFixed(4)}</span>
        </div>
      </div>
      <div className="create-hub__field-grid">
        <div className="create-hub__field create-hub__field--full">
          <label className="create-hub__label" htmlFor="edit-addr">
            Street Address
          </label>
          <input
            id="edit-addr"
            className="create-hub__input"
            type="text"
            placeholder="e.g. 500 Folsom St"
            value={address}
            onChange={e => update("address", e.target.value)}
          />
        </div>
        <div className="create-hub__field">
          <label className="create-hub__label" htmlFor="edit-city">
            City
          </label>
          <input
            id="edit-city"
            className="create-hub__input"
            type="text"
            placeholder="e.g. San Francisco"
            value={city}
            onChange={e => update("city", e.target.value)}
          />
        </div>
        <div className="create-hub__field">
          <label className="create-hub__label" htmlFor="edit-state">
            State
          </label>
          <input
            id="edit-state"
            className="create-hub__input"
            type="text"
            placeholder="e.g. CA"
            value={state}
            onChange={e => update("state", e.target.value)}
          />
        </div>
        <div className="create-hub__field">
          <label className="create-hub__label" htmlFor="edit-zip">
            ZIP Code
          </label>
          <input
            id="edit-zip"
            className="create-hub__input"
            type="text"
            placeholder="e.g. 94105"
            value={zip}
            onChange={e => update("zip", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

EditLocationMap.propTypes = {
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  address: PropTypes.string.isRequired,
  city: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  zip: PropTypes.string.isRequired,
  update: PropTypes.func.isRequired,
};

/* ── PropTypes ── */
StationDetailView.propTypes = {
  station: PropTypes.object.isRequired,
  allStations: PropTypes.array,
  onBack: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

DeleteConfirmModal.propTypes = {
  show: PropTypes.bool.isRequired,
  stationName: PropTypes.string.isRequired,
  deleting: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
